/**
 * One panel's read, rendered — or the one line that says why it is not.
 *
 * `.docs/20` §A's page is eleven panels over seven reads, and the failure mode that
 * matters is a single read refusing: `analytics_effective_rate` reads
 * `setting('billing.rate_floor')` and `time_entry`, and it must not take the MRR
 * hero down with it. Every read is `inline` (`data.ts`), so the error arrives on the
 * result rather than at the route boundary, and this is where it is said.
 *
 * The sentence is `api/problem.ts` `messageOf` — `.docs/reports/19-report.md`: "the
 * `detail` is the sentence the API wrote for this reader; `error.message` is the
 * generic title", and six panels in session 19 wrote their own before that was one
 * function.
 *
 * The render prop is what keeps `data` non-optional inside a panel: a section takes
 * the shape it draws, not a query result, so nothing below this file branches on
 * loading again.
 */

import type { UseQueryResult } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type { ApiError } from '../../../api/hooks.js'
import { messageOf } from '../../../api/problem.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { sentenceCase } from '@fareast/shared'

export type PanelProps<T> = {
  readonly query: UseQueryResult<T, ApiError>
  /** What this panel is, as the middle of a sentence — "the recurring revenue series". */
  readonly subject: string
  readonly children: (data: T) => ReactNode
}

export function Panel<T>({ query, subject, children }: PanelProps<T>) {
  if (query.data !== undefined) return <>{children(query.data)}</>

  return (
    <Empty>
      {query.error === null
        ? `Loading ${subject}.`
        : `${sentenceCase(subject)} could not be loaded — ${messageOf(query.error)}`}
    </Empty>
  )
}
